import {
  Box,
  Paper,
  Title,
  Text,
  Button,
  Flex,
  Container,
  Stepper,
  TextInput,
  PasswordInput,
  PinInput,
  Group,
  Anchor,
  Alert,
  Checkbox,
} from "@mantine/core";
import { IconMail, IconLock, IconArrowLeft, IconShieldCheck, IconInfoCircle } from "@tabler/icons-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import trpc, { saveToken } from "@/shared/utils/trpc";
import { successNotification } from "@/shared/utils/successNotification";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUser } from "@/context/UserContext";

const RESEND_COOLDOWN_SEC = 30;
const TERMS_URL = "https://gtsetu.com/terms/";
const PRIVACY_URL = "https://gtsetu.com/privacy/";

function trpcErrorMessage(err: unknown): string {
  const e = err as { data?: { json?: { message?: string } }; shape?: { message?: string }; message?: string };
  return (
    e?.data?.json?.message ||
    e?.shape?.message ||
    (typeof e?.message === "string" ? e.message : "") ||
    ""
  );
}

function isAlreadyVerifiedActivationMessage(msg: string): boolean {
  return msg.toLowerCase().includes("already verified");
}

function ActivateAccountPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetchUser } = useUser();

  const [active, setActive] = useState(0);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTermsAndPrivacy, setAcceptedTermsAndPrivacy] = useState(false);
  const [activationToken, setActivationToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [alreadyVerifiedNotice, setAlreadyVerifiedNotice] = useState(false);

  useEffect(() => {
    const q = searchParams.get("email")?.trim();
    if (q) setEmail(q);
  }, [searchParams]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setInterval(() => {
      setResendIn((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [resendIn]);

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const passwordError = password && password.length < 8 ? "Password must be at least 8 characters" : null;
  const confirmPasswordError =
    confirmPassword && password !== confirmPassword ? "Passwords do not match" : null;
  const passwordStepValid = password.length >= 8 && password === confirmPassword && acceptedTermsAndPrivacy;

  const startResendCooldown = useCallback(() => {
    setResendIn(RESEND_COOLDOWN_SEC);
  }, []);

  const handleSendCode = async () => {
    if (!isValidEmail(email.trim())) return;
    setLoading(true);
    setAlreadyVerifiedNotice(false);
    try {
      const out = await trpc.auth.requestActivationOtp.mutate({ email: email.trim() });
      if (out.alreadyVerified) {
        setAlreadyVerifiedNotice(true);
        return;
      }
      successNotification({
        title: "Code sent",
        message: "If this email is awaiting activation, check your inbox for a 6-digit code.",
      });
      setActive(1);
      startResendCooldown();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendIn > 0 || loading) return;
    setLoading(true);
    setAlreadyVerifiedNotice(false);
    try {
      const out = await trpc.auth.requestActivationOtp.mutate({ email: email.trim() });
      if (out.alreadyVerified) {
        setAlreadyVerifiedNotice(true);
        setActive(0);
        return;
      }
      successNotification({
        title: "Code sent",
        message: "A new code has been sent if this email is awaiting activation.",
      });
      startResendCooldown();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (code.trim().length !== 6) return;
    setLoading(true);
    try {
      const out = await trpc.auth.verifyActivationOtp.mutate({
        email: email.trim(),
        code: code.trim(),
      });
      setActivationToken(out.activationToken);
      setActive(2);
    } catch (err) {
      handleTrpcError(err);
      const msg = trpcErrorMessage(err);
      if (isAlreadyVerifiedActivationMessage(msg)) {
        setAlreadyVerifiedNotice(true);
        setActive(0);
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!passwordStepValid || !activationToken) return;
    setLoading(true);
    try {
      const data = await trpc.auth.setPasswordWithActivationToken.mutate({
        activationToken,
        newPassword: password,
        acceptedTermsAndPrivacy,
      });
      if (!("token" in data)) return;
      saveToken(data.token);
      await refetchUser();
      successNotification({
        title: "Account activated",
        message: "Your password is set. Welcome to GTsetu.",
      });
      navigate("/create-profile", { replace: true });
    } catch (err: unknown) {
      handleTrpcError(err);
      const trpcMessage = trpcErrorMessage(err);
      if (isAlreadyVerifiedActivationMessage(trpcMessage)) {
        setAlreadyVerifiedNotice(true);
        setActivationToken("");
        setCode("");
        setActive(0);
        return;
      }
      if (
        trpcMessage.includes("expired") ||
        trpcMessage.includes("Request a new code") ||
        trpcMessage.includes("activation session")
      ) {
        setActivationToken("");
        setCode("");
        setActive(1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box mih="100vh" py={40}>
      <Container size="lg">
        <Flex direction="column" align="center" gap={4} mb="xl">
          <img src="/gtLogo.svg" alt="GTsetu" style={{ height: 100, width: "auto", marginBottom: 4 }} />
        </Flex>

        <Paper
          shadow="sm"
          p={{ base: "lg", sm: "xl" }}
          radius="lg"
          maw={560}
          mx="auto"
          style={{ border: "1px solid #E5E7EB" }}
        >
          <Title order={2} fz={{ base: 20, sm: 24 }} mb="xs">
            Activate your account
          </Title>

          <Text fz="sm" c="slate.6" mb="lg">
            Confirm your email, enter the one-time code we send you, then choose a password.
          </Text>

          {alreadyVerifiedNotice && (
            <Alert
              icon={<IconInfoCircle size={18} />}
              title="Account already verified"
              color="blue"
              variant="light"
              mb="lg"
            >
              <Text size="sm" mb="sm">
                This account is already verified. Log in to continue. If you forgot your password, use Forgot password.
              </Text>
              <Group gap="lg">
                <Anchor component={Link} to="/login" size="sm" fw={500}>
                  Log in
                </Anchor>
                <Anchor component={Link} to="/forgot-password" size="sm" fw={500}>
                  Forgot password
                </Anchor>
              </Group>
            </Alert>
          )}

          <Stepper active={active} mb="xl">
            <Stepper.Step label="Email" description="Send code">
              <TextInput
                label="Email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                leftSection={<IconMail size={18} color="#9CA3AF" />}
                mb="md"
                required
              />
              <Button
                fullWidth
                size="md"
                onClick={handleSendCode}
                loading={loading}
                disabled={loading || !isValidEmail(email.trim())}
              >
                Send verification code
              </Button>
            </Stepper.Step>

            <Stepper.Step label="Verify" description="Enter OTP">
              <Text fz="sm" c="dimmed" mb="sm">
                Code sent to <strong>{email}</strong>
              </Text>
              <Flex justify="center" mb="md">
                <PinInput length={6} type="number" size="md" value={code} onChange={setCode} />
              </Flex>
              <Group justify="space-between" mb="md">
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => {
                    setAlreadyVerifiedNotice(false);
                    setActive(0);
                  }}
                  disabled={loading}
                >
                  Change email
                </Button>
                <Button variant="light" size="xs" onClick={handleResendCode} disabled={loading || resendIn > 0}>
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </Button>
              </Group>
              <Button
                fullWidth
                size="md"
                leftSection={<IconShieldCheck size={18} />}
                onClick={handleVerifyOtp}
                loading={loading}
                disabled={loading || code.trim().length !== 6}
              >
                Continue
              </Button>
            </Stepper.Step>

            <Stepper.Step label="Password" description="Finish setup">
              <PasswordInput
                label={
                  <Text fz="sm" fw={500} mb={4}>
                    New password{" "}
                    <Text span c="red">
                      *
                    </Text>
                  </Text>
                }
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                leftSection={<IconLock size={18} color="#9CA3AF" />}
                error={passwordError}
                mb="md"
              />
              <PasswordInput
                label={
                  <Text fz="sm" fw={500} mb={4}>
                    Confirm password{" "}
                    <Text span c="red">
                      *
                    </Text>
                  </Text>
                }
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                leftSection={<IconLock size={18} color="#9CA3AF" />}
                error={confirmPasswordError}
                mb="xl"
              />

              <Checkbox
                size="xs"
                mb="lg"
                checked={acceptedTermsAndPrivacy}
                onChange={(e) => setAcceptedTermsAndPrivacy(e.currentTarget.checked)}
                label={
                  <Text component="span" fz="xs" c="gray.6" lh={1.45}>
                    By continuing, you agree to our{" "}
                    <Anchor
                      href={TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      c="gray.6"
                      fw={500}
                      fz="inherit"
                      underline="always"
                    >
                      Terms & Conditions
                    </Anchor>{" "}
                    and{" "}
                    <Anchor
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      c="gray.6"
                      fw={500}
                      fz="inherit"
                      underline="always"
                    >
                      Privacy Policy
                    </Anchor>
                    .
                  </Text>
                }
              />

              <Button
                fullWidth
                size="md"
                onClick={handleSetPassword}
                loading={loading}
                disabled={!passwordStepValid || loading}
              >
                Activate & continue
              </Button>
            </Stepper.Step>

          </Stepper>

          <Flex justify="center" mt="lg">
            <Anchor component={Link} to="/login" size="sm" c="#2563EB">
              <Flex align="center" gap={4}>
                <IconArrowLeft size={14} />
                Back to Login
              </Flex>
            </Anchor>
          </Flex>
        </Paper>
      </Container>
    </Box>
  );
}

export default ActivateAccountPage;
